import { useDispatch, useSelector } from 'react-redux';
import { logoutReduxState, refreshTokenRequest } from '../redux/slices/authSlice';
import { RootState, persistor } from '../redux/store';

// User information interface
export interface User {
  id: string;
  name: string;
  email: string;
  role: string;
  token?: string;
}

export const useUser = () => {
  const dispatch = useDispatch();
  const { user, loading, error, tokens } = useSelector((state: RootState) => state.auth);
  const safeTokens = tokens ?? { access: null, refresh: null };

  // Safely check if a JWT token is still valid (not expired)
  const isJwtValid = (jwt?: string | null): boolean => {
    if (!jwt) return false;
    const parts = jwt.split('.');
    if (parts.length !== 3) return false;
    try {
      const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
      if (!payload || typeof payload.exp !== 'number') return false;
      const nowInSeconds = Math.floor(Date.now() / 1000);
      return payload.exp > nowInSeconds + 5; // small skew buffer
    } catch {
      return false;
    }
  };

  // Logout function
  const logout = async (): Promise<void> => {
    // Clear all persisted storage
    await persistor.purge();
    dispatch(logoutReduxState());
  };

  // Refresh token function
  const refreshToken = async (): Promise<void> => {
    if (safeTokens.refresh) {
      dispatch(refreshTokenRequest());
    }
  };

  // Check if user is authenticated
  const isAuthenticated = (): boolean => {
    return isJwtValid(safeTokens.access || null);
  };

  return {
    user,
    logout,
    loading,
    error,
    isAuthenticated,
    refreshToken,
  };
};
